import {
  useState, useEffect, useMemo, useRef,
} from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, Checkbox,
  FormControlLabel, FormGroup, Switch, Divider,
  FormControl, InputLabel, Select, MenuItem,
  CircularProgress, LinearProgress,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/core/styles';
import { alpha } from '@material-ui/core/styles/colorManipulator';
import { toggleLoading } from '../../context/LayoutContext';
import api from '../../services/api';
import { getError } from '../../utils/getResponseError';
import { toCurrency, moneyToMask, maskToMoney } from '../../utils/mask';
import { basicDate, parseISO } from '../../utils/dateFormatter';
import Notification from '../../utils/notification';
import { VALOR_MIN, VALOR_MAX } from '../../utils/constants';
import calcularProximaFatura from './calcularProximaFatura';

function buildResumoTextual(edicao, produtoValor) {
  if (!edicao) return 'Nenhuma edição anterior.';
  const produtoLabel = produtoValor ? `valor do produto (${toCurrency(produtoValor, true)})` : 'valor do produto';
  const frases = [];
  const campos = [
    { key: 'dia_vencimento', label: 'Dia de vencimento' },
    { key: 'dias_antes', label: 'Dias antes' },
    {
      key: 'valor',
      label: 'Valor',
      format: (v) => (v !== null && v !== undefined ? toCurrency(v, true) : produtoLabel),
    },
    {
      key: 'data_termino',
      label: 'Data de término',
      format: (v) => (v ? basicDate(parseISO(v)) : 'não definida'),
      nullAction: { add: 'definida para', remove: 'removida' },
    },
    { key: 'vencido_multa', label: 'Multa', suffix: '%' },
    { key: 'vencido_juros', label: 'Juros', suffix: '%' },
    { key: 'vencido_dias_max', label: 'Prazo máx.', suffix: ' dias' },
    {
      key: 'vencido_aceita',
      label: 'Aceita vencido',
      format: (v) => (v ? 'Sim' : 'Não'),
    },
    {
      key: 'desconto',
      label: 'Desconto',
      format: (v) => (v ? 'Ativo' : 'Inativo'),
    },
    { key: 'desconto_prazo', label: 'Prazo de desconto', suffix: ' dias' },
    {
      key: 'desconto_valor',
      label: 'Valor do desconto',
      format: (v) => (v !== null && v !== undefined ? toCurrency(v, true) : '—'),
    },
  ];

  campos.forEach((campo) => {
    const ant = edicao[`anterior_${campo.key}`];
    const post = edicao[`posterior_${campo.key}`];
    const antNull = ant === null || ant === undefined;
    const postNull = post === null || post === undefined;
    if (antNull && postNull) return;

    let formatAnt;
    let formatPost;

    if (campo.format) {
      formatAnt = campo.format(ant);
      formatPost = campo.format(post);
    } else {
      const suffix = campo.suffix || '';
      formatAnt = !antNull ? String(ant) + suffix : 'não definido';
      formatPost = !postNull ? String(post) + suffix : 'não definido';
    }

    if (campo.nullAction && antNull && !postNull) {
      frases.push(`${campo.label} ${campo.nullAction.add} ${formatPost}.`);
    } else if (campo.nullAction && !antNull && postNull) {
      frases.push(`${campo.label} ${campo.nullAction.remove} (era ${formatAnt}).`);
    } else {
      frases.push(`${campo.label} alterado de ${formatAnt} para ${formatPost}.`);
    }
  });

  if (frases.length === 0) {
    return 'Nenhum campo alterado.';
  }
  return frases.join(' ');
}

const useStyles = makeStyles((theme) => ({
  backdrop: {
    backgroundColor: alpha(theme.palette.primary.main, 0.5),
  },
}));

export default function EditarCobrancaModal({
  open,
  onClose,
  cobranca,
  onSaved,
  onFaturaEmitida,
  layoutDispatch,
  onViewHistorico,
}) {
  const classes = useStyles();

  const [diaVencimento, setDiaVencimento] = useState('');
  const [diasAntes, setDiasAntes] = useState('');
  const [valor, setValor] = useState('');
  const [usarValorProduto, setUsarValorProduto] = useState(false);
  const [dataTermino, setDataTermino] = useState('');
  const [dataTerminoAtivo, setDataTerminoAtivo] = useState(false);

  const [vencidoMulta, setVencidoMulta] = useState(0.5);
  const [vencidoJuros, setVencidoJuros] = useState(0.5);
  const [vencidoAceita, setVencidoAceita] = useState(false);
  const [vencidoDiasMax, setVencidoDiasMax] = useState(30);
  const [descontoAtivo, setDescontoAtivo] = useState(false);
  const [descontoPrazo, setDescontoPrazo] = useState(5);
  const [descontoValor, setDescontoValor] = useState('');

  const [saving, setSaving] = useState(false);
  const [showFaturaEmAtrasoDialog, setShowFaturaEmAtrasoDialog] = useState(false);
  const [faturaEmAtraso, setFaturaEmAtraso] = useState(null);
  const [emitting, setEmitting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const pollIntervalRef = useRef(null);
  const prevFaturaIdRef = useRef(null);

  useEffect(() => {
    if (open && cobranca) {
      const valorAtual = cobranca.valor !== null && cobranca.valor !== undefined
        ? toCurrency(cobranca.valor, false)
        : '';

      setDiaVencimento(cobranca.dia_vencimento ? cobranca.dia_vencimento.toString() : '');
      setDiasAntes(cobranca.dias_antes ? cobranca.dias_antes.toString() : '');
      setValor(valorAtual);
      setUsarValorProduto(cobranca.valor === null || cobranca.valor === undefined);
      setDataTermino(cobranca.data_termino ? cobranca.data_termino.split('T')[0] : '');

      setVencidoMulta(cobranca.vencido_multa || 0.5);
      setVencidoJuros(cobranca.vencido_juros || 0.5);
      setVencidoAceita(cobranca.vencido_aceita ?? false);
      setVencidoDiasMax(cobranca.vencido_dias_max || 30);
      setDescontoAtivo(cobranca.desconto ?? false);
      setDescontoPrazo(cobranca.desconto_prazo || 5);
      setDescontoValor(cobranca.desconto_valor ? toCurrency(cobranca.desconto_valor, false) : '');
    }
  }, [open, cobranca]);

  const proximaFatura = useMemo(() => {
    if (!cobranca || !diaVencimento || !diasAntes) {
      return null;
    }

    try {
      const parsedValor = valor ? maskToMoney(valor) : null;
      const result = calcularProximaFatura({
        diaVencimento: Number(diaVencimento),
        diasAntes: Number(diasAntes),
        periodo: cobranca.periodo,
        ultimaFatura: cobranca.Faturas && cobranca.Faturas[0] ? cobranca.Faturas[0] : null,
        valor: usarValorProduto ? null : parsedValor,
        produto: cobranca.Produto || undefined
      });

      return result;
    } catch (err) {
      return null;
    }
  }, [diaVencimento, diasAntes, valor, usarValorProduto, cobranca]);

  const handleValorChange = (e) => {
    if (!usarValorProduto) {
      setValor(moneyToMask(e.target.value));
    }
  };

  const handleUsarValorProdutoChange = (e) => {
    setUsarValorProduto(e.target.checked);
  };

  const handleCloseFaturaDialog = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setPolling(false);
    setPollCount(0);
    setEmitting(false);
    setShowFaturaEmAtrasoDialog(false);
    setFaturaEmAtraso(null);
  };

  const handleConfirmEmitirAgora = async () => {
    prevFaturaIdRef.current = cobranca.Faturas && cobranca.Faturas[0]
      ? cobranca.Faturas[0].id_fatura
      : null;
    setEmitting(true);
    toggleLoading(layoutDispatch);
    try {
      await api.post(`/cobranca/${cobranca.id_cobranca}/emitir-agora`);
      toggleLoading(layoutDispatch);
      setEmitting(false);
      setPolling(true);
    } catch (error) {
      toggleLoading(layoutDispatch);
      setEmitting(false);
      Notification({
        type: 'report',
        message: getError(error),
        variant: 'contained',
        color: 'secondary',
      });
      handleCloseFaturaDialog();
      if (onSaved) onSaved();
      if (onClose) onClose();
    }
  };

  useEffect(() => {
    if (!polling) return undefined;

    const MAX_ATTEMPTS = 20;
    const cobrancaId = cobranca.id_cobranca;
    const prevId = prevFaturaIdRef.current;
    let attempts = 0;

    const finish = (success) => {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
      setPolling(false);
      setPollCount(0);
      setShowFaturaEmAtrasoDialog(false);
      setFaturaEmAtraso(null);
      if (success) {
        Notification({
          type: 'shipped',
          message: 'Fatura emitida com sucesso',
          variant: 'contained',
          color: 'success',
        });
        if (onFaturaEmitida) onFaturaEmitida();
      } else {
        Notification({
          type: 'report',
          message: 'Tempo limite excedido aguardando emissão da fatura',
          variant: 'contained',
          color: 'secondary',
        });
      }
      if (onSaved) onSaved();
      if (onClose) onClose();
    };

    const checkFaturas = async () => {
      attempts += 1;
      setPollCount(attempts);
      try {
        const { data } = await api.get(`/cobranca/${cobrancaId}/faturas`, {
          params: { limit: 1, page: 1 },
        });
        const newId = data.dados && data.dados[0] ? data.dados[0].id_fatura : null;
        if (newId !== prevId) {
          finish(true);
          return;
        }
      } catch (e) {
        // ignore individual poll errors
      }
      if (attempts >= MAX_ATTEMPTS) {
        finish(false);
      }
    };

    pollIntervalRef.current = setInterval(checkFaturas, 3000);

    return () => {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    };
  }, [polling]); // eslint-disable-line

  const handleSave = async () => {
    setSaving(true);
    toggleLoading(layoutDispatch);

    try {
      const parsedValor = valor ? maskToMoney(valor) : null;
      if (parsedValor !== null && (parsedValor < VALOR_MIN || parsedValor > VALOR_MAX)) {
        return Notification({
          type: 'report',
          message: `O valor diferenciado deve estar entre R$ ${VALOR_MIN.toFixed(2).replace('.', ',')} e R$ ${VALOR_MAX.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
          variant: 'contained',
          color: 'secondary',
        });
      }
      if (dataTerminoAtivo && !dataTermino) {
        return Notification({
          type: 'report',
          message: 'Informe a data de término ou desative a opção.',
          variant: 'contained',
          color: 'secondary',
        });
      }
      if (!usarValorProduto && !valor) {
        return Notification({
          type: 'report',
          message: 'Informe um valor diferenciado ou marque "Usar valor do produto".',
          variant: 'contained',
          color: 'secondary',
        });
      }
      if (!diaVencimento) {
        return Notification({
          type: 'report',
          message: 'Informe o dia de vencimento.',
          variant: 'contained',
          color: 'secondary',
        });
      }
      if (!diasAntes) {
        return Notification({
          type: 'report',
          message: 'Informe os dias antes do envio.',
          variant: 'contained',
          color: 'secondary',
        });
      }
      const payload = {
        dia_vencimento: Number(diaVencimento) || cobranca.dia_vencimento,
        dias_antes: Number(diasAntes) || cobranca.dias_antes,
        valor: usarValorProduto ? null : parsedValor,
        data_termino: dataTerminoAtivo ? (dataTermino || null) : null,
        vencido_aceita: vencidoAceita,
        desconto: descontoAtivo,
        ...(vencidoAceita ? {
          vencido_dias_max: Number(vencidoDiasMax) || 30,
          vencido_multa: Number(vencidoMulta) || 0.5,
          vencido_juros: Number(vencidoJuros) || 0.5,
        } : {}),
        ...(descontoAtivo ? {
          desconto_prazo: Number(descontoPrazo) || 0,
          desconto_valor: descontoValor ? maskToMoney(descontoValor) : 0,
        } : {}),
      };

      const { data } = await api.put(`/cobranca/${cobranca.id_cobranca}`, payload);

      if (data.faturaEmAtraso !== null && data.faturaEmAtraso !== undefined) {
        setFaturaEmAtraso(data.faturaEmAtraso);
        setShowFaturaEmAtrasoDialog(true);
      } else {
        Notification({
          type: 'shipped',
          message: 'Cobrança atualizada com sucesso',
          variant: 'contained',
          color: 'success',
        });
        if (onSaved) onSaved();
        if (onClose) onClose();
      }
    } catch (error) {
      Notification({
        type: 'report',
        message: getError(error),
        variant: 'contained',
        color: 'secondary',
      });
    } finally {
      setSaving(false);
      toggleLoading(layoutDispatch);
    }
  };

  const handleCancel = () => {
    if (onClose) onClose();
  };

  if (!cobranca) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open && !showFaturaEmAtrasoDialog}
        onClose={handleCancel}
        fullWidth
        maxWidth="md"
        BackdropProps={{ className: classes.backdrop }}
      >
        <DialogTitle>Editar Cobrança Recorrente</DialogTitle>
        <DialogContent>
          <Box py={2}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Dia de Vencimento"
                  value={diaVencimento}
                  onChange={(e) => setDiaVencimento(e.target.value)}
                  inputProps={{ min: 1, max: 31 }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Dias Antes do Envio"
                  value={diasAntes}
                  onChange={(e) => setDiasAntes(e.target.value)}
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={6}>
                <Box mb={1}>
                  <TextField
                    fullWidth
                    label="Valor Diferenciado"
                    value={valor}
                    onChange={handleValorChange}
                    disabled={usarValorProduto}
                    inputProps={{
                      maxLength: 18,
                      min: VALOR_MIN,
                      max: VALOR_MAX
                    }}
                  />
                </Box>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={usarValorProduto}
                      onChange={handleUsarValorProdutoChange}
                    />
                  )}
                  label={`Usar valor do produto${cobranca?.Produto?.valor ? ` ${toCurrency(cobranca.Produto.valor, true)}` : ''}`}
                />
              </Grid>

              <Grid item xs={6}>
                <FormGroup row>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={dataTerminoAtivo}
                        onChange={() => setDataTerminoAtivo((prev) => !prev)}
                      />
                    )}
                    label="Programar data de término?"
                  />
                </FormGroup>
                {dataTerminoAtivo && (
                  <TextField
                    fullWidth
                    type="date"
                    label="Data de Término"
                    value={dataTermino}
                    onChange={(e) => setDataTermino(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    style={{ marginTop: 8 }}
                  />
                )}
              </Grid>

              <Grid item xs={12}>
                <Divider style={{ margin: '16px 0' }} />
              </Grid>

              <Grid item xs={12}>
                <FormGroup row>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={vencidoAceita}
                        onChange={() => setVencidoAceita((prev) => !prev)}
                      />
                    )}
                    label="Aceitar pagamento após o vencimento?"
                  />
                </FormGroup>
              </Grid>

              {vencidoAceita && (
                <Grid container item xs={12} spacing={2}>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel id="edit-prazo-max-label">Prazo máximo</InputLabel>
                      <Select
                        labelId="edit-prazo-max-label"
                        id="edit-prazo-max"
                        value={vencidoDiasMax}
                        name="vencido_dias_max"
                        onChange={(e) => setVencidoDiasMax(e.target.value)}
                      >
                        <MenuItem value={30}>30 dias</MenuItem>
                        <MenuItem value={60}>60 dias</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel id="edit-multa-label">Multa</InputLabel>
                      <Select
                        labelId="edit-multa-label"
                        id="edit-multa"
                        value={vencidoMulta}
                        name="vencido_multa"
                        onChange={(e) => setVencidoMulta(e.target.value)}
                      >
                        <MenuItem value={0.25}>0.25% do valor</MenuItem>
                        <MenuItem value={0.5}>0.5% do valor</MenuItem>
                        <MenuItem value={0.75}>0.75% do valor</MenuItem>
                        <MenuItem value={1}>1% do valor</MenuItem>
                        <MenuItem value={1.25}>1.25% do valor</MenuItem>
                        <MenuItem value={1.5}>1.5% do valor</MenuItem>
                        <MenuItem value={1.75}>1.75% do valor</MenuItem>
                        <MenuItem value={2}>2% do valor (máx. por lei)</MenuItem>
                        <MenuItem value={5}>5% do valor</MenuItem>
                        <MenuItem value={10}>10% do valor</MenuItem>
                        <MenuItem value={15}>15% do valor</MenuItem>
                        <MenuItem value={20}>20% do valor</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel id="edit-juros-label">Juros</InputLabel>
                      <Select
                        labelId="edit-juros-label"
                        id="edit-juros"
                        value={vencidoJuros}
                        name="vencido_juros"
                        onChange={(e) => setVencidoJuros(e.target.value)}
                      >
                        <MenuItem value={0.25}>0.25% ao mês</MenuItem>
                        <MenuItem value={0.5}>0.5% ao mês</MenuItem>
                        <MenuItem value={0.75}>0.75% ao mês</MenuItem>
                        <MenuItem value={1}>1% ao mês</MenuItem>
                        <MenuItem value={1.25}>1.25% ao mês</MenuItem>
                        <MenuItem value={1.5}>1.5% ao mês</MenuItem>
                        <MenuItem value={1.75}>1.75% ao mês</MenuItem>
                        <MenuItem value={2}>2% ao mês (máx. por lei)</MenuItem>
                        <MenuItem value={5}>5% ao mês</MenuItem>
                        <MenuItem value={10}>10% ao mês</MenuItem>
                        <MenuItem value={15}>15% ao mês</MenuItem>
                        <MenuItem value={20}>20% ao mês</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              <Grid item xs={12}>
                <FormGroup row>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={descontoAtivo}
                        onChange={() => setDescontoAtivo((prev) => !prev)}
                      />
                    )}
                    label="Oferecer desconto para pagamento antecipado?"
                  />
                </FormGroup>
              </Grid>

              {descontoAtivo && (
                <Grid container item xs={12} spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Valor do desconto"
                      value={descontoValor}
                      onChange={(e) => setDescontoValor(moneyToMask(e.target.value))}
                      inputProps={{
                        maxLength: 18,
                        min: VALOR_MIN,
                        max: VALOR_MAX,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel id="edit-desconto-prazo-label">Desconto válido até</InputLabel>
                      <Select
                        labelId="edit-desconto-prazo-label"
                        id="edit-desconto-prazo"
                        value={descontoPrazo}
                        name="desconto_prazo"
                        onChange={(e) => setDescontoPrazo(e.target.value)}
                      >
                        <MenuItem value={0}>0 dias</MenuItem>
                        <MenuItem value={1}>1 dia</MenuItem>
                        <MenuItem value={2}>2 dias</MenuItem>
                        <MenuItem value={3}>3 dias</MenuItem>
                        <MenuItem value={4}>4 dias</MenuItem>
                        <MenuItem value={5}>5 dias</MenuItem>
                        <MenuItem value={6}>6 dias</MenuItem>
                        <MenuItem value={7}>7 dias</MenuItem>
                        <MenuItem value={10}>10 dias</MenuItem>
                        <MenuItem value={15}>15 dias</MenuItem>
                        <MenuItem value={30}>30 dias</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider style={{ margin: '16px 0' }} />
              </Grid>

              {cobranca.CobrancaEdicoes && cobranca.CobrancaEdicoes.length > 0 && (
                <Grid item xs={6}>
                  <Typography variant="h6" style={{ marginBottom: 8 }}>
                    Última Edição
                  </Typography>
                  <Box pl={2}>
                    <Typography variant="body2" gutterBottom>
                      <strong>Data:</strong>
                      {' '}
                      {cobranca.CobrancaEdicoes[0].editado_em
                        ? basicDate(parseISO(cobranca.CobrancaEdicoes[0].editado_em))
                        : '—'}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Usuário:</strong>
                      {' '}
                      {cobranca.CobrancaEdicoes[0].Usuario
                        ? cobranca.CobrancaEdicoes[0].Usuario.nome
                        : '—'}
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: 8, lineHeight: 1.6 }}>
                      {buildResumoTextual(cobranca.CobrancaEdicoes[0], cobranca?.Produto?.valor)}
                    </Typography>
                    {onViewHistorico && (
                      <Box mt={1}>
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => {
                            if (onViewHistorico) onViewHistorico();
                            if (onClose) onClose();
                          }}
                        >
                          Ver histórico completo
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

              <Grid
                item
                xs={cobranca.CobrancaEdicoes && cobranca.CobrancaEdicoes.length > 0 ? 6 : 12}
              >
                <Typography variant="h6" style={{ marginBottom: 16 }}>
                  Próxima Fatura Estimada
                </Typography>
                <Box pl={2}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Data de Emissão:</strong>
                        {' '}
                        {proximaFatura ? basicDate(proximaFatura.dataEmissao) : '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Data de Vencimento:</strong>
                        {' '}
                        {proximaFatura ? basicDate(proximaFatura.dataVencimento) : '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Valor:</strong>
                        {' '}
                        {proximaFatura && proximaFatura.valor !== null
                          ? toCurrency(proximaFatura.valor, true)
                          : '—'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: 16 }}>
          <Button onClick={handleCancel} color="default">
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {faturaEmAtraso && (
        <Dialog
          open={showFaturaEmAtrasoDialog}
          onClose={handleCloseFaturaDialog}
          fullWidth
          maxWidth="sm"
          BackdropProps={{ className: classes.backdrop }}
        >
          <DialogTitle>Ciclo de fatura perdido</DialogTitle>
          <DialogContent>
            {polling ? (
              <Box py={3}>
                <Typography variant="body1" align="center" gutterBottom>
                  Aguardando emissão da fatura...
                </Typography>
                <Box mt={2}>
                  <LinearProgress />
                </Box>
              </Box>
            ) : (
              <Box py={1}>
                <Alert severity="warning">
                  A edição alterou o ciclo de cobrança e uma fatura ficou sem emissão.
                  Se não emitida agora, não será gerada automaticamente.
                </Alert>
                <Box
                  mt={2}
                  p={2}
                  style={{ backgroundColor: '#f5f5f5', borderRadius: 4 }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography
                        variant="caption"
                        display="block"
                        color="textSecondary"
                      >
                        Data de emissão
                      </Typography>
                      <Typography variant="subtitle1">
                        <strong>{basicDate(parseISO(faturaEmAtraso.emissao))}</strong>
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography
                        variant="caption"
                        display="block"
                        color="textSecondary"
                      >
                        Vencimento
                      </Typography>
                      <Typography variant="subtitle1">
                        <strong>{basicDate(parseISO(faturaEmAtraso.vencimento))}</strong>
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography
                        variant="caption"
                        display="block"
                        color="textSecondary"
                      >
                        Valor
                      </Typography>
                      <Typography variant="subtitle1">
                        <strong>{toCurrency(faturaEmAtraso.valor, true)}</strong>
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
                <Box mt={2}>
                  <Typography variant="body2">
                    Deseja emitir esta fatura agora?
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions style={{ padding: 16 }}>
            {!polling && (
              <>
                <Button
                  onClick={() => {
                    handleCloseFaturaDialog();
                    if (onSaved) onSaved();
                    if (onClose) onClose();
                  }}
                  color="default"
                  disabled={emitting}
                >
                  Não emitir
                </Button>
                <Button
                  onClick={handleConfirmEmitirAgora}
                  color="primary"
                  variant="contained"
                  disabled={emitting}
                >
                  {emitting ? <CircularProgress size={20} /> : 'Sim, emitir agora'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
